import * as React from 'react'

import { observer } from 'mobx-react'

import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import {
  CompanyGroupingOption,
  CompanyListSelector,
  ICompanyMetadata,
} from '~/client/src/shared/components/CompanyListSelector'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'

import EditableTagsCell from './EditableTagsCell'

interface IProps {
  companyIds: string[]
  companies: Company[]
  companyMetadata?: Map<string, ICompanyMetadata>
  isBookable: boolean
  getCompanyById: (id: string) => Company | undefined
  onChange: (companyIds: string[]) => void
  /** How the selector surfaces (popover vs full-panel sheet). */
  presentation?: 'popover' | 'sheet'
}

function PermittedCompaniesCell({
  companyIds,
  companies,
  companyMetadata,
  isBookable,
  getCompanyById,
  onChange,
  presentation,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  // Off => "Not Set", so the selector shows nothing selected. On + empty means
  // "all allowed" → show every company checked.
  const selectedCompanyIds = React.useMemo(
    () =>
      !isBookable
        ? new Set<string>()
        : companyIds.length === 0
          ? new Set(companies.map(company => company.id))
          : new Set(companyIds),
    [isBookable, companyIds, companies],
  )

  const selectedCompanies = React.useMemo(
    () =>
      companyIds.map(id => getCompanyById(id)).filter((c): c is Company => !!c),
    [companyIds, getCompanyById],
  )

  // Only while bookable does an empty/every-company selection mean "All". While
  // off, every column reads "Not Set".
  const showAll =
    isBookable &&
    (companyIds.length === 0 ||
      (companies.length > 0 &&
        companies.every(company => companyIds.includes(company.id))))

  const handleApply = (newIds: Set<string>) => {
    setIsOpen(false)
    // Off + empty apply is a no-op so the hidden selection isn't wiped.
    if (!isBookable && newIds.size === 0) return
    // Normalizing an all-companies selection to [] ("All") is the save handler's
    // job — it can enable the resource off the raw selection while persisting [].
    onChange(Array.from(newIds))
  }

  return (
    <EditableTagsCell
      isBookable={isBookable}
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      cellAffordance="chevron"
      triggerLabel={Localization.translator.permittedCompanies}
      presentation={presentation}
      popoverClassName="tw:w-[400px] tw:h-[min(560px,var(--available-height,85vh))]"
      emptyDisplay={
        showAll ? (
          <span className="tw:text-body-md tw:leading-[20px] tw:text-foreground">
            {Localization.translator.all}
          </span>
        ) : undefined
      }
      tags={
        showAll
          ? []
          : selectedCompanies.map(company => ({
              key: company.id,
              label: company.name,
              avatar: (
                <Avatar
                  src={company.avatarUrl}
                  size={AvatarSize.XMinuscule}
                  avatarType={AvatarType.Company}
                />
              ),
              onDismiss: () =>
                onChange(
                  companyIds.filter(companyId => companyId !== company.id),
                ),
            }))
      }
      popoverContent={
        // Fill the container edge-to-edge (both popover and drawer): the selector
        // owns its insets, and its virtualized list needs a bounded flex-fill box.
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
          <CompanyListSelector
            selectedCompanyIds={selectedCompanyIds}
            companies={companies}
            companyMetadata={companyMetadata}
            defaultGrouping={CompanyGroupingOption.TYPE}
            title={Localization.translator.selectPermittedCompanies}
            showActions
            disableApplyWhenEmpty
            onClose={() => setIsOpen(false)}
            onApply={handleApply}
            className="tw:flex-1 tw:min-h-0"
          />
        </div>
      }
    />
  )
}

export default observer(PermittedCompaniesCell)
